// Cliente HTTP do backoffice.
//
// Caminho relativo, nunca URL absoluta: em desenvolvimento o Vite faz proxy
// para a 8030, e em produção a interface é servida do mesmo domínio. É o que
// mantém o on-premise sem configuração de CORS (SPEC §13).

/** Erro em RFC 7807, que é o que o backoffice devolve (PATTERNS §3). */
export class ErroApi extends Error {
  constructor(
    readonly status: number,
    readonly tipo: string,
    readonly titulo: string,
    readonly detalhe?: string,
  ) {
    // O `detail` é a mensagem útil — vem do banco ou da regra de negócio e
    // explica melhor que qualquer tradução nossa. O `title` é genérico.
    super(detalhe || titulo);
  }
}

async function requisitar<T>(caminho: string, opcoes: RequestInit = {}): Promise<T> {
  // `content-type` só quando há corpo: um GET com content-type faz alguns
  // proxies tratarem a requisição como preflight.
  const cabecalhos = new Headers(opcoes.headers);
  if (opcoes.body) cabecalhos.set("content-type", "application/json");
  // `credentials` explícito: o cookie de sessão é `HttpOnly` e só vai se o
  // `fetch` o mandar. É o padrão do navegador, e escrito aqui para que uma
  // refatoração não o remova sem perceber que desligou a autenticação.
  const r = await fetch(`/api${caminho}`, {
    ...opcoes,
    headers: cabecalhos,
    credentials: "same-origin",
  });
  if (!r.ok) {
    const corpo = (await r.json().catch(() => ({}))) as Record<string, string>;
    throw new ErroApi(
      r.status,
      corpo.type ?? "desconhecido",
      corpo.title ?? r.statusText,
      corpo.detail,
    );
  }
  return r.status === 204 ? (undefined as T) : ((await r.json()) as T);
}

export const obter = <T>(c: string): Promise<T> => requisitar<T>(c);
export const enviar = <T>(c: string, corpo: unknown): Promise<T> =>
  requisitar<T>(c, { method: "POST", body: JSON.stringify(corpo) });
export const alterar = <T>(c: string, corpo: unknown): Promise<T> =>
  requisitar<T>(c, { method: "PATCH", body: JSON.stringify(corpo) });
export const remover = <T>(c: string): Promise<T> => requisitar<T>(c, { method: "DELETE" });

/**
 * Upload de arquivo: corpo binário puro, nome e categoria em cabeçalho.
 *
 * Percent-encode obrigatório — cabeçalho HTTP é latin-1, e um nome de arquivo
 * com acento (que é a regra nos portais brasileiros: "Municípios.zip") quebra
 * a requisição antes de sair.
 */
export async function subirArquivo(
  camadaId: string,
  arquivo: File,
  categoria?: string | null,
): Promise<ResultadoUpload> {
  const cabecalhos: Record<string, string> = {
    "content-type": "application/octet-stream",
    "x-nome-arquivo": encodeURIComponent(arquivo.name),
  };
  if (categoria) cabecalhos["x-categoria"] = encodeURIComponent(categoria);

  const r = await fetch(`/api/camadas/${camadaId}/upload`, {
    method: "POST",
    headers: cabecalhos,
    body: arquivo,
  });
  if (!r.ok) {
    const c = (await r.json().catch(() => ({}))) as Record<string, string>;
    throw new ErroApi(r.status, c.type ?? "desconhecido", c.title ?? r.statusText, c.detail);
  }
  return (await r.json()) as ResultadoUpload;
}

// --- Contratos ------------------------------------------------------------

export type Conector = { tipo: string; descricao: string };

export type Tema = { id: string; nome: string; descricao: string | null; camadas: number };

export type Fonte = {
  id: string;
  orgao: string;
  url_oficial: string;
  licenca: string | null;
  atribuicao: string | null;
  periodicidade_oficial: string | null;
  restricao_uso: string | null;
  contem_dado_pessoal: boolean;
  exige_contorno: boolean;
  /** `publica` = base de terceiro (exige licença); `privada` = achado de cliente. */
  natureza: "publica" | "privada";
  publicavel: boolean;
};

export type Camada = {
  id: string;
  nome: string;
  tema_id: string;
  fonte_id: string;
  tipo_conector: string;
  endpoint_url: string | null;
  parametros: Record<string, unknown>;
  cadencia_sync: string | null;
  modo_publicacao: string;
  ativo: boolean;
  fonte_orgao: string;
  fonte_publicavel: boolean;
  /** `espelho` = a resposta É a base; `acumulativo` = ela acrescenta (ADR-021). */
  modo_ingestao: "espelho" | "acumulativo";
  /** Execuções vazias seguidas que viram alerta `fonte_silenciosa`. `null` = não alertar. */
  execucoes_vazias_para_alertar: number | null;
  /** `null` = base pública compartilhada. Preenchido = dado privado do cliente. */
  conta_id: string | null;
  projeto_id: string | null;
  conta_nome: string | null;
  projeto_nome: string | null;
  /** A fonte declara dado pessoal? Se sim, a camada precisa de decisão. */
  fonte_dado_pessoal: boolean;
  /** `null` = ninguém decidiu; `[]` = decidido que não há o que suprimir. */
  atributos_suprimidos: string[] | null;
  aguardando: number;
  validadas: number;
  arquivada_em: string | null;
  ultima_versao: number | null;
  ultima_execucao: { status: string; executado_em: string; mensagem: string | null } | null;
};

export type Feicao = {
  id: string;
  codigo_externo: string | null;
  categoria: string | null;
  uf: string | null;
  status: string;
  atributos: Record<string, unknown>;
  motivo_rejeicao: string | null;
  tem_geometria: boolean;
  /** `Polygon`, `MultiLineString`… `null` quando não há geometria. */
  tipo_geometria: string | null;
  ingerido_em: string;
  validado_em: string | null;
  validado_por: string | null;
};

/**
 * Uma página de feições.
 *
 * Os agregados vêm `null` nas páginas de CONTINUAÇÃO (quando a requisição levou
 * `cursor`): eles não mudam entre páginas da mesma consulta, e o corte por
 * geometria avalia `ST_GeometryType` em cada linha da camada — recalculá-lo a
 * cada "carregar mais" faria a rolagem ficar mais lenta quanto mais o curador
 * rolasse. Quem consome lê os agregados da primeira página.
 *
 * `null` e não `{}`: vazio diria "medi e não achei nada", que é outra afirmação.
 */
export type ListaFeicoes = {
  feicoes: Feicao[];
  /** Por status, sobre a camada INTEIRA. Ignora `uf`, `categoria` e `codigo`. */
  totais: Record<string, number> | null;
  /** Sob TODOS os filtros da requisição. É o denominador de "exibindo n de N". */
  total_filtrado: number | null;
  /** Tipos de geometria do que está `validado` — o que vai sair na promoção. */
  geometrias: Record<string, number> | null;
  /** Acima de 1 é defeito: cliente Esri fixa um `geometryType` e descarta o resto. */
  familias_de_geometria: number | null;
  geometrias_por_status: Record<string, Record<string, number>> | null;
  limite: number;
  /** Opaco. `null` na última página. */
  proximo_cursor: string | null;
  /** Só `proximo_cursor !== null`, mantido porque o contrato já o tinha. */
  cortado: boolean;
};

export type Execucao = {
  id: string;
  tipo: string;
  status: string;
  registros_processados: number;
  categoria: string | null;
  mensagem: string | null;
  avisos: string | null;
  executado_em: string;
  executado_por: string | null;
};

export type Versao = {
  id: string;
  numero: number;
  total_feicoes: number;
  selada_em: string | null;
  promovida_em: string;
  promovida_por: string;
  atributos_suprimidos: string[];
};

export type Colecao = {
  id_publico: string;
  camada_id: string;
  titulo: string;
  descricao: string | null;
  graca_ate: string | null;
  versao_vigente: number | null;
  total_feicoes: number | null;
  selada_em: string | null;
  versao_anterior: number | null;
  camada: string;
  licenca: string | null;
  atribuicao: string | null;
  /** Média medida sobre amostra da versão vigente. `null` = versão vazia. */
  bytes_por_feicao: number | null;
  /** O que uma página de 5.000 feições pesaria em GeoJSON. */
  bytes_por_pagina: number | null;
  /** Q5, SPEC §12: "esta camada em GeoJSON vai truncar no cliente". */
  avisa_geojson: boolean;
};

/**
 * O que a validação em lote devolve.
 *
 * `filtro` é o que o servidor APLICOU, não o que se pediu — e é o mesmo que ele
 * gravou na auditoria. Os campos vêm com `null` explícito onde não houve corte:
 * "validou 24.116" sem o critério não responde à única pergunta que se faz a uma
 * trilha de auditoria.
 */
export type ResultadoLote = {
  validadas: number;
  filtro: { status: string; uf: string | null; categoria: string | null };
  motivo: string | null;
};

/**
 * O que a decisão por SELEÇÃO devolve.
 *
 * `nao_encontradas` acima de zero significa id que não é desta camada ou que não
 * existe mais. Relatar só `decididas` faria a tela dizer "300 validadas" tendo
 * mudado 280.
 */
/**
 * Geometria de UMA feição, para o mini-mapa da linha expandida.
 *
 * `geometria: null` NÃO é erro — a feição existe e simplesmente não tem
 * geometria aproveitável. `bbox` é `[minLon, minLat, maxLon, maxLat]`, para a
 * tela enquadrar sem calcular.
 */
export type GeometriaFeicao = {
  geometria: GeometriaGeoJson | null;
  bbox: [number, number, number, number] | null;
  atributos: Record<string, unknown>;
  codigo_externo: string | null;
};

export type ResultadoDecisaoLote = {
  acao: "validar" | "rejeitar";
  pedidas: number;
  decididas: number;
  nao_encontradas: number;
  motivo: string | null;
};

/** O que a publicação devolve. `graca_ate` é a que VALEU, não a que se pediu. */
export type ResultadoPublicacao = {
  ok: true;
  versaoId: string;
  versaoAnteriorId: string | null;
  gracaAte: string | null;
};

export type Operador = {
  usuario: string;
  nome: string;
  ativo: boolean;
  criado_em: string;
  criado_por: string;
  ultimo_acesso: string | null;
};

export type Conta = {
  id: string;
  nome: string;
  /** Prefixo das coleções privadas desta conta. `null` = só consome base pública. */
  slug: string | null;
  status: string;
  criado_em: string;
  limite_requisicoes_dia: number | null;
  limite_feicoes_dia: number | null;
  chaves_ativas: number;
};

export type ChaveResumo = {
  id: string;
  prefixo: string;
  status: string;
  emitida_em: string;
  revogada_em: string | null;
  colecoes: string[];
};

/** O que a emissão devolve. `chave` existe UMA vez: o banco guarda o hash. */
export type ChaveEmitida = {
  id: string;
  prefixo: string;
  chave: string;
  /** EXPANDIDAS: com `projeto_id`, é aqui que se vê o que foi concedido. */
  colecoes: string[];
  projeto_id: string | null;
};

export type Projeto = {
  id: string;
  nome: string;
  criado_em: string;
  camadas: number;
  colecoes: number;
};

export type Auditoria = {
  ator: string;
  acao: string;
  alvo: string | null;
  detalhe: Record<string, unknown> | null;
  criado_em: string;
};

export type Alerta = {
  camada_id: string;
  camada: string;
  tipo: "falhou" | "atrasou" | "cadencia_invalida";
  detalhe: string;
  desde: string | null;
};

export type ItemAgendador = {
  id: string;
  nome: string;
  cadencia_sync: string;
  modo_publicacao: string;
  proxima_execucao: string | null;
  cadencia_invalida: string | null;
  alerta: Alerta | null;
};

export type PainelAgendador = { fuso: string; camadas: ItemAgendador[]; alertas: Alerta[] };

export type GeometriaGeoJson = { type: string; coordinates: unknown };

export type FeicaoMapa = {
  id: string;
  camada_id: string;
  codigo_externo: string | null;
  categoria: string | null;
  uf: string | null;
  atributos: Record<string, unknown>;
  status: string;
  geometria: GeometriaGeoJson;
};

export type Totais = { publicado: number; aguardando_validacao: number; validado: number };

export type Mapa = {
  camadas: { id: string; nome: string; colecao: string | null; versao_vigente_id: string | null }[];
  publicado: FeicaoMapa[];
  aguardando_validacao: FeicaoMapa[];
  validado: FeicaoMapa[];
  totais_brutos: Totais;
  totais_com_geometria: Totais;
  totais_com_geometria_sem_filtro_espacial: Totais;
  limite_aplicado: number;
  cortado: { publicado: boolean; aguardando_validacao: boolean; validado: boolean };
  ufs_disponiveis: string[];
  categorias_disponiveis: string[];
  tolerancia_simplificacao: number;
};

export type ResultadoUpload = {
  execucao_id: string;
  arquivo: string;
  categoria: string | null;
  epsg_origem: number;
  removidas: number;
  brutas: number;
  inseridas: number;
  atualizadas: number;
  inalteradas: number;
  semGeometria: number;
  avisos: string[];
};

export type ResultadoPromocao = { versaoId: string; numero: number; feicoes: number };

/**
 * Os números da Visão geral.
 *
 * `ingerido`, `aguardando`, `validado` e `rejeitado` falam da MESMA tabela, e os
 * três últimos a particionam: o `CHECK` da coluna de status admite três valores e
 * só três, então `aguardando + validado + rejeitado = ingerido` é invariante de
 * schema. **Não é um afunilamento** — uma feição está em exatamente um estado, e
 * validar tudo deixa `aguardando` em zero com `validado` no total.
 *
 * `em_versoes` e `publicado` vêm de outra tabela e não se somam aos de cima.
 */
export type Painel = {
  funil: {
    /** Tudo em staging agora, em qualquer status. */
    ingerido: number;
    /** Subconjunto de `ingerido`: `aguardando_validacao`. */
    aguardando: number;
    /** Subconjunto de `ingerido`: `validado`. Não inclui versão selada. */
    validado: number;
    /** Subconjunto de `ingerido`: `rejeitado`. Fecha a partição. */
    rejeitado: number;
    /** Em versão selada. NÃO se soma a `validado`: seria a mesma feição duas vezes. */
    em_versoes: number;
    /** Nas versões vigentes das coleções no ar. Pode passar `ingerido` se o staging foi limpo. */
    publicado: number;
    camadas: number;
    fontes: number;
    camadas_com_fila: number;
    colecoes_no_ar: number;
  };
  travas: {
    fontes_sem_licenca: number;
    camadas_sem_executar: number;
    execucoes_com_erro: number;
  };
  fila: { id: string; nome: string; fonte: string; conector: string; aguardando: number }[];
  colecoes: { id_publico: string; versao: number | null }[];
  auditoria: { ator: string; acao: string; alvo: string | null; criado_em: string }[];
};

// --- Sessão ------------------------------------------------------------------
//
// A senha aparece uma vez, aqui, e vira cookie `HttpOnly` do lado do servidor.
// Não há `localStorage`: o que o JavaScript não consegue ler, um XSS não rouba.

export type Sessao = { operador: string; origem?: string };

/**
 * Entra.
 *
 * Basic numa requisição só — é o formato que o back office já entende, e que
 * `curl` continua usando. `btoa` sobre UTF-8 quebra com acento, então a senha
 * passa por `TextEncoder` antes: senha com "ç" é comum e falharia aqui, longe
 * de onde se olharia.
 */
export async function entrar(usuario: string, senha: string): Promise<Sessao> {
  const bytes = new TextEncoder().encode(`${usuario}:${senha}`);
  const basic = btoa(String.fromCharCode(...bytes));
  const r = await fetch("/api/sessao", {
    method: "POST",
    headers: { authorization: `Basic ${basic}` },
    credentials: "same-origin",
  });
  if (!r.ok) {
    const c = (await r.json().catch(() => ({}))) as Record<string, string>;
    throw new ErroApi(r.status, c.type ?? "desconhecido", c.title ?? r.statusText, c.detail);
  }
  return (await r.json()) as Sessao;
}

/** Quem está operando. 401 aqui significa "mostre a tela de entrada". */
export const quemSou = (): Promise<Sessao> => obter<Sessao>("/sessao");

/**
 * Sai.
 *
 * Nunca levanta erro: "sair" que falha deixa a pessoa presa numa tela que ela
 * pediu para abandonar. O cookie é apagado pelo servidor; se a requisição não
 * chegar, a sessão expira sozinha em doze horas.
 */
export async function sair(): Promise<void> {
  await fetch("/api/sessao", { method: "DELETE", credentials: "same-origin" }).catch(
    () => undefined,
  );
}

/** O que `POST /conectores/ogc/descobrir` lê do `GetCapabilities` de um WFS. */
export type CapacidadesWfs = {
  versao: string | null;
  tipos: { nome: string; titulo: string | null; crsPadrao: string | null }[];
  formatos: string[];
  tetoPorPagina: number | null;
  /** O `outputFormat` JSON que o conector deve pedir; `null` = a fonte só serve GML. */
  formato_sugerido: string | null;
};

export const descobrirWfs = (
  endpoint_url: string,
  fonte_id: string | null,
): Promise<CapacidadesWfs> =>
  enviar<CapacidadesWfs>("/conectores/ogc/descobrir", {
    endpoint_url,
    fonte_id: fonte_id ?? undefined,
  });
